import { EPersona } from '@sharedInterfaces/EPersona';
import { ELinks } from '@sharedInterfaces/globalEnums';
import { checkPermissions } from '@sharedInterfaces/IPermissions';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { ITabConfig } from '@src/App/Components/Core/Layouts/LayoutElements/MvpTabContainer/ITabConfig';
import type { IMvpQuickAction, IMvpRole } from '@src/App/Components/Core/MvpSubNav/MvpSubNav';
import type { Entity } from '@src/Objects/Entity';
import { selectCurrentUserOnboardingTalk } from '@store/reducer/employeeTalksReducer';
import type { AppState } from '@store/store';
import {
    AccountCircle,
    Business,
    Groups2,
    Home,
    Mail,
    School,
    SupervisedUserCircle,
} from '@mui/icons-material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SmsIcon from '@mui/icons-material/Sms';
import { useTheme } from '@mui/material';
import React, { createContext, useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

// ─── Nav Items ───────────────────────────────────────────────────────────────

export interface INavItem {
    id: string;
    path: string;
    title: string;
    icon: React.ReactNode;
    group: 'main' | 'sales';
    forceActive?: boolean;
    activeColor?: string;
}

// ─── SubNav Config ────────────────────────────────────────────────────────────

export interface ISubNavConfig {
    currentPageTitle?: string;
    tabs?: ITabConfig[];
    roles?: IMvpRole[];
    activeRole?: string;
    onRoleChange?: (roleId: string) => void;
    quickActions?: IMvpQuickAction[];
    shareEntity?: Entity;
}

// ─── Context ──────────────────────────────────────────────────────────────────

interface INavigationContextValue {
    /** Computed nav items — readonly, derived from Redux state */
    navItems: INavItem[];
    /** SubNav config set by the current page */
    subNav: ISubNavConfig | null;
    setSubNav: (config: ISubNavConfig | null) => void;
}

const NavigationContext = createContext<INavigationContextValue>({
    navItems: [],
    subNav: null,
    setSubNav: () => {},
});

// ─── Provider ─────────────────────────────────────────────────────────────────

export function NavigationProvider({ children }: { children: React.ReactNode }) {
    const theme = useTheme();

    const lang = useSelector((s: AppState) => s.employee.language);
    const permissions = useSelector((s: AppState) => s.permissions);
    const featureFlags = useSelector((s: AppState) => s.company.featureFlags);
    const personas = useSelector((s: AppState) => s.personas);
    const currentEmployee = useSelector((s: AppState) => s.employee);
    const OUs = useSelector((s: AppState) => s.OUs);
    const onboardingTalk = useSelector(selectCurrentUserOnboardingTalk);

    const [subNav, setSubNav] = useState<ISubNavConfig | null>(null);

    const navItems = useMemo<INavItem[]>(() => {
        const langStrings = getLanguageTexts(lang).navigation;
        const employeeTalkStrings = getLanguageTexts(lang).employeeTalk;
        const items: INavItem[] = [];

        // Onboarding — einheitliche Logik für Desktop + Mobile
        if (onboardingTalk && !onboardingTalk.employeeConfirmed) {
            items.push({
                id: 'onboarding',
                path: `/${ELinks.EMPLOYEE_TALKS}/${onboardingTalk.id}`,
                title: 'Onboarding',
                icon: <School />,
                group: 'main',
                forceActive: true,
                activeColor: theme.palette.warning.main,
            });
        }

        items.push({
            id: 'dashboard',
            path: `/${ELinks.DASHBOARD}`,
            title: langStrings.dashboard,
            icon: <Home />,
            group: 'main',
        });

        items.push({
            id: 'profile',
            path: `/${ELinks.PROFILE_SHOW}`,
            title: langStrings.profileOverview,
            icon: <AccountCircle />,
            group: 'main',
        });

        if (
            featureFlags.employeeTalks &&
            (checkPermissions('EmployeeTalks', 'Retrieve', permissions) ||
                checkPermissions('EmployeeTalks', 'RetrieveOwn', permissions))
        ) {
            items.push({
                id: 'employeeTalks',
                path: `/${ELinks.EMPLOYEE_TALKS}`,
                title: employeeTalkStrings.employeeTalks,
                icon: <SmsIcon />,
                group: 'main',
            });
        }

        if (personas.includes(EPersona.FK)) {
            items.push({
                id: 'myTeam',
                path: `/${ELinks.MY_TEAM}`,
                title: langStrings.myTeam,
                icon: <SupervisedUserCircle />,
                group: 'main',
            });
        }

        items.push({
            id: 'company',
            path: `/${ELinks.COMPANY}`,
            title: 'Unternehmen',
            icon: <Business />,
            group: 'main',
        });

        if (featureFlags.employees) {
            items.push({
                id: 'employees',
                path: `/${ELinks.PEOPLE_EMPLOYEES}`,
                title: langStrings.employees.replace('Mitarbeiter', 'Mitarbeitende'),
                icon: <Groups2 />,
                group: 'main',
            });
        }

        if (featureFlags.organizations) {
            const currentEmployeeOrgUnit = OUs.find((ou) => ou.employees.includes(currentEmployee.id));
            items.push({
                id: 'orgUnits',
                path: currentEmployeeOrgUnit
                    ? `/${ELinks.PEOPLE_ORGUNITS}/${currentEmployeeOrgUnit.id}`
                    : `/${ELinks.PEOPLE_ORGUNITS}`,
                title: langStrings.orgUnits,
                icon: <Business />,
                group: 'main',
            });
        }

        if (featureFlags.messages) {
            items.push({
                id: 'messages',
                path: `/${ELinks.MESSAGES}`,
                title: 'Nachrichten',
                icon: <Mail />,
                group: 'main',
            });
        }

        if (featureFlags.opportunities) {
            items.push({
                id: 'opportunities',
                path: `/${ELinks.SALES_OPPORTUNITIES}`,
                title: 'Anfragen',
                icon: <AssignmentIcon />,
                group: 'sales',
            });
        }

        return items;
    }, [onboardingTalk, lang, permissions, featureFlags, personas, OUs, currentEmployee.id, theme.palette.warning.main]);

    const contextValue = useMemo(
        () => ({ navItems, subNav, setSubNav }),
        [navItems, subNav],
    );

    return (
        <NavigationContext.Provider value={contextValue}>
            {children}
        </NavigationContext.Provider>
    );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useNavigation = () => useContext(NavigationContext);
